import React, { useEffect, useState } from "react";
import { useParams } from "react-router";

import useAPI from "services/Api";
import ActivityPanel from "./ActivityPanel";
import PatientProfile from "pages/PatientProfile";
import RomsPanel from "./RomsPanel";
import SessionsPanel from "./SessionsPanel";
import UsageStatsPanel from "./UsageStatsPanel";
import { useNotificationManager } from "notifications/NotificationManager";

const PatientUsage = ({ componentName = "[PatientActivity]" }) => {

    const [currentWeek, setCurrentWeek] = useState(null);

    const [coachingPlanDataFound, setCoachingPlanDataFound] = useState(false);
    const [isLoadingCoachingPlanData, setIsLoadingCoachingPlanData] = useState(true);

    const [rawActivityData, setRawActivityData] = useState(null);
    const [rawActivityDataMissing, setRawActivityDataMissing] = useState(false);
    const [isLoadingRawActivityData, setIsLoadingRawActivityData] = useState<boolean>(true);

    const [rawRomData, setRawRomData] = useState(null);
    const [rawRomDataMissing, setRawRomDataMissing] = useState(false);
    const [isLoadingRawRomData, setIsLoadingRawRomData] = useState<boolean>(true);

    const [rawSessionData, setRawSessionData] = useState(null);
    const [rawSessionDataMissing, setRawSessionDataMissing] = useState(false);
    const [isLoadingRawSessionData, setIsLoadingRawSessionData] = useState<boolean>(true);

    const [rawUsageStatsData, setRawUsageStatsData] = useState(null);
    const [rawUsageStatsDataMissing, setRawUsageStatsDataMissing] = useState(false);
    const [isLoadingRawUsageStatsData, setIsLoadingRawUsageStatsData] = useState<boolean>(true);

    const API = useAPI();
    const notificationManager = useNotificationManager();
    const patientId = (useParams() as { id: string }).id;

    const getActivityData = async () => {
        await API.api_Get(
            componentName,
            `/exercises/?visit__patient=${patientId}&limit=5000&most_recent_coaching_plan=true`,
            notificationManager,
            (response) => {
                if (!response?.data?.results?.length) {
                    console.warn(`No activity data found for patient id[${patientId}]`);
                    setRawActivityDataMissing(true);
                }
                else {
                    // console.log(`Found activity data: ${JSON.stringify(response.data.results, null, 2)}`);
                    setRawActivityData(response.data.results);
                }
                setIsLoadingRawActivityData(false);
            }
        );
    };

    const getRomData = async () => {
        await API.api_Get(
            componentName,
            `/roms/?patient=${patientId}&limit=5000&most_recent_coaching_plan=true`,
            notificationManager,
            (response) => {
                if (!response?.data?.results?.length) {
                    console.warn(`No rom data found for patient id[${patientId}]`);
                    setRawRomDataMissing(true);
                }
                else {
                    // console.log(`Found rom data: ${JSON.stringify(response.data.results, null, 2)}`);
                    setRawRomData(response.data.results);
                }
                setIsLoadingRawRomData(false);
            }
        );
    };

    const getSessionData = async () => {
        await API.api_Get(
            componentName,
            `/session_history/?patient_id=${patientId}&limit=5000&most_recent_coaching_plan=true`,
            notificationManager,
            (response) => {
                if (!response?.data || Object.keys(response.data).length === 0) {
                    console.warn(`No session data found for patient id[${patientId}]`);
                    setRawSessionDataMissing(true);
                }
                else {
                    // console.log(`Found session data: ${JSON.stringify(response.data.results, null, 2)}`);
                    setRawSessionData(response.data);
                }
                setIsLoadingRawSessionData(false);
            }
        );
    };

    const getUsageStatsData = async () => {
        await API.api_Get(
            componentName,
            `/patient_usage_stats/?detail=true&patient_id=${patientId}&exclude_visits_without_coaching_plans=true`,
            notificationManager,
            (response) => {
                const dataFound = response?.data?.results[0]?.plan_stats?.[Object.keys(response?.data?.results[0]?.plan_stats || {}).at(-1)]?.plan_weeks;
                if (!dataFound) {
                    console.warn(`No usage stats data found for patient id[${patientId}]`);
                    setRawUsageStatsDataMissing(true);
                }
                else {
                    // console.log(`Found usage stats data: ${JSON.stringify(response.data.results, null, 2)}`);
                    setRawUsageStatsData(response.data.results[0].plan_stats);
                }
                setIsLoadingRawUsageStatsData(false);
            }
        );
    };

    const loadData = async () => {
        Promise
            .all([
                getActivityData(),
                getSessionData(),
                getRomData(),
                getUsageStatsData()
            ])
            .then(
                () => {
                }
            )
            .catch(
                (error) => {
                    notificationManager.addError(error);
                }
            )
    };

    const onPatientData = (patientData) => {
        if (patientData.coaching_plan) {
            setCurrentWeek(patientData.coaching_plan["plan_week"]);
            setCoachingPlanDataFound(true);
        }
        else if (patientData.last_coaching_plan_week) {
            setCurrentWeek(patientData.last_coaching_plan_week);
            setCoachingPlanDataFound(true);
        }

        setIsLoadingCoachingPlanData(false);
    };

    useEffect(
        () => {
            void loadData();
        },
        []
    );

    return (
        <PatientProfile
            parentComponentName={componentName}
            // Callbacks
            onPatientData={onPatientData}
        >
            {
                !isLoadingCoachingPlanData && (
                    !coachingPlanDataFound || (rawActivityDataMissing && rawRomDataMissing && rawSessionDataMissing && rawUsageStatsDataMissing)
                        ? (
                            <div className="w-full h-full justify-center gap-6 items-center inline-flex flex-col">
                                <div className="w-14 h-14 p-3.5 bg-[#ffe9e2] rounded-full border border-gray-50 justify-center items-center inline-flex">
                                    <img className="w-7 h-7 relative flex-col justify-start items-start flex" src="/icons/data.svg" alt="icon"/>
                                </div>
                                <div className="text-wrap w-1/2 [@media(min-width:1600px)]:w-1/4 break-words">
                                    <div className="self-stretch text-center text-gray-700 text-3xl font-semibold leading-[38px]">No Data</div>
                                    <div className="break-words inline-block text-center text-gray-500 text-base font-normal">
                                        There is no activity data to display for this patient yet. Once they have an active coaching plan and begin completing VET activities, you can track their usage data here.
                                    </div>
                                </div>
                            </div>
                        )
                        : (
                            <div className="w-full h-[99%] max-[1800px]:h-[99%]	grid min-[1200px]:grid-rows-2 grid-cols-2 max-[1200px]:grid-cols-1 gap-4 mx-4 my-2">
                                <div className="justify-center items-center inline-flex max-[1200px]:w-full max-[1200px]:h-[400px] min-w-0 min-h-0 relative rounded-[10px] shadow border border-gray-200">
                                    <ActivityPanel
                                        coachingPlanDataFound={coachingPlanDataFound}
                                        currentWeek={currentWeek}
                                        dataMissing={rawActivityDataMissing}
                                        isLoading={isLoadingRawActivityData}
                                        rawActivityData={rawActivityData}
                                    />
                                </div>
                                <div className="justify-center items-center inline-flex max-[1200px]:w-full max-[1200px]:h-[400px] min-w-0 min-h-0 relative rounded-[10px] shadow border border-gray-200">
                                    <RomsPanel
                                        coachingPlanDataFound={coachingPlanDataFound}
                                        currentWeek={currentWeek}
                                        dataMissing={rawRomDataMissing}
                                        isLoading={isLoadingRawRomData}
                                        rawRomData={rawRomData}
                                    />
                                </div>
                                <div className="justify-start items-center inline-flex max-[1200px]:w-full max-[1200px]:h-[400px] min-w-0 min-h-0 relative rounded-[10px] shadow border border-gray-200">
                                    <SessionsPanel
                                        coachingPlanDataFound={coachingPlanDataFound}
                                        dataMissing={rawSessionDataMissing}
                                        isLoading={isLoadingRawSessionData}
                                        patientId={patientId}
                                        rawSessionData={rawSessionData}
                                    />
                                </div>
                                <div className="justify-center items-center inline-flex max-[1200px]:w-full max-[1200px]:h-[400px] min-w-0 min-h-0 relative rounded-[10px] shadow border border-gray-200">
                                    <UsageStatsPanel
                                        coachingPlanDataFound={coachingPlanDataFound}
                                        dataMissing={rawUsageStatsDataMissing}
                                        isLoading={isLoadingRawUsageStatsData}
                                        currentWeek={currentWeek}
                                        rawUsageStatsData={rawUsageStatsData}
                                    />
                                </div>
                            </div>
                        )
                )
            }
        </PatientProfile>
    );
};

export default PatientUsage;
